// src/scenes/global/Sidebar.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Collapse,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    ProSidebar,
    Menu,
    MenuItem as SidebarMenuItem,
} from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Link } from "react-router-dom";
import { tokens } from "../../theme";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TimelineIcon from "@mui/icons-material/Timeline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ListIcon from "@mui/icons-material/List";
import LogoutIcon from "@mui/icons-material/Logout";
import OfficeIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAuth } from "../../authContext/AuthContext";

const iconStyles = {
    dashboard: { color: "#1976d2" },       // Blue
    liquidOffice: { color: "#009688" },      // Teal
    tracking: { color: "#ff9800" },          // Orange
    payroll: { color: "#4caf50" },           // Green
    users: { color: "#9c27b0" },             // Purple
    logout: { color: "#f44336" },            // Red
};

const Item = ({ title, to, icon, selected, setSelected }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <SidebarMenuItem
            active={selected === title}
            style={{ color: colors.grey[400], paddingLeft: "40px" }}
            onClick={() => setSelected(title)}
            icon={icon}
        >
            <Typography>{title}</Typography>
            <Link to={to} />
        </SidebarMenuItem>
    );
};

const Sidebar = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isCollapsed, setIsCollapsed] = useState(isMobile);
    const [selected, setSelected] = useState("Dashboard");
    const { user, logout } = useAuth();

    // Separate state variables for collapsible sections
    const [trackingOpen, setTrackingOpen] = useState(false);
    const [payrollOpen, setPayrollOpen] = useState(false);

    useEffect(() => {
        setIsCollapsed(isMobile);
    }, [isMobile]);

    return (
        <Box
            sx={{
                "& .pro-sidebar-inner": {
                    background: `${colors.primary[400]} !important`,
                },
                "& .pro-icon-wrapper": { backgroundColor: "transparent !important" },
                "& .pro-inner-item": { padding: "5px 35px 5px 20px !important" },
                "& .pro-inner-item:hover": { color: "#868dfb !important" },
                "& .pro-menu-item.active": { color: "#6870fa !important" },
            }}
        >
            <ProSidebar collapsed={isCollapsed}>
                <Menu iconShape="square">
                    {/* Collapse Button */}
                    <SidebarMenuItem
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        icon={<MenuOutlinedIcon />}
                        style={{ margin: "10px 0 20px 0", color: colors.grey[100] }}
                    >
                        {!isCollapsed && (
                            <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                                <Typography variant="h3" color={colors.grey[100]}>
                                    CCPS HR
                                </Typography>
                                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                                    <MenuOutlinedIcon />
                                </IconButton>
                            </Box>
                        )}
                    </SidebarMenuItem>

                    {/* Logo Section */}
                    {!isCollapsed && (
                        <Box mb="20px" display="flex" justifyContent="center" alignItems="center">
                            <img
                                src="https://www.collierschools.com/cms/lib/FL01903251/Centricity/Template/GlobalAssets/images///logos/collier-logo.png"
                                alt="CCPS Logo"
                                width="200px"
                                height="80px"
                                style={{ cursor: "pointer", borderRadius: "8px" }}
                            />
                        </Box>
                    )}

                    {/* Main Menu Items */}
                    <Item
                        title="Dashboard"
                        to="/"
                        icon={<DashboardIcon style={iconStyles.dashboard} />}
                        selected={selected}
                        setSelected={setSelected}
                    />

                    <SidebarMenuItem
                        icon={<OfficeIcon style={iconStyles.liquidOffice} />}
                        style={{ color: colors.grey[300], paddingLeft: "20px" }}
                        onClick={() =>
                            window.open(
                                "https://liquidOffice.collierschools.com",
                                "_blank",
                                "noopener,noreferrer,width=1200,height=800"
                            )
                        }
                    >
                        <Typography variant="h6" color={colors.grey[300]}>
                            CCPS LiquidOffice
                        </Typography>
                    </SidebarMenuItem>

                    {/* Collapsible Tracking Section */}
                    <SidebarMenuItem
                        icon={<TimelineIcon style={iconStyles.tracking} />}
                        onClick={() => setTrackingOpen(!trackingOpen)}
                        style={{ color: colors.grey[300], paddingLeft: "20px" }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color={colors.grey[300]}>
                                Tracking
                            </Typography>
                            <IconButton>
                                {trackingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={trackingOpen}>
                        <Box pl="20px">
                            <Item
                                title="Separation Tracking"
                                to="/invsep_tracking"
                                icon={<ListIcon style={iconStyles.tracking} />}
                                selected={selected}
                                setSelected={setSelected}
                            />
                        </Box>
                    </Collapse>

                    {/* Collapsible Payroll Section */}
                    <SidebarMenuItem
                        icon={<MonetizationOnIcon style={iconStyles.payroll} />}
                        onClick={() => setPayrollOpen(!payrollOpen)}
                        style={{ color: colors.grey[300], paddingLeft: "20px" }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" color={colors.grey[300]}>
                                Payroll
                            </Typography>
                            <IconButton>
                                {payrollOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                    </SidebarMenuItem>
                    <Collapse in={payrollOpen}>
                        <Box pl="20px">
                            <Item
                                title="Payroll CSV"
                                to="/payroll"
                                icon={<ListIcon style={iconStyles.payroll} />}
                                selected={selected}
                                setSelected={setSelected}
                            />
                        </Box>
                    </Collapse>
                    {/* Admin‑only Users Section */}
                    {user && user.roleId === 1 && (
                        <>
                            <Item
                                title="Users"
                                to="/users"
                                icon={<GroupIcon style={iconStyles.users} />}
                                selected={selected}
                                setSelected={setSelected}
                            />
       
                        </>
                    )}


                    {/* Logout Option */}
                    <SidebarMenuItem
                        icon={<LogoutIcon style={iconStyles.logout} />}
                        onClick={() => logout()}
                        style={{
                            color: colors.grey[300],
                            paddingLeft: "20px",
                            marginTop: "auto",
                            cursor: "pointer",
                        }}
                    >
                        <Typography variant="h6" color={colors.grey[300]}>
                            Logout
                        </Typography>
                    </SidebarMenuItem>
                </Menu>
            </ProSidebar>
        </Box>
    );
};

export default Sidebar;
